// ENG-032: the single navigation of the admin platform. Every admin / analytics / feedback page is one item here,
// and the router (src/index.tsx) gates each route with the same `access` value, so the sidebar and the gate
// can never disagree about who sees what.

import { UserRole } from "../../contexts/AuthContext";

/** "admin" = ADMIN only; "observer" = ANALYTICS or ADMIN (the backend's require_analytics). */
export type PlatformAccess = "admin" | "observer";

export interface PlatformNavItem {
    path: string; // absolute hash-route path
    title: string;
    subtitle: string;
    icon: string;
    access: PlatformAccess;
}

export interface PlatformNavGroup {
    key: string;
    title: string;
    items: PlatformNavItem[];
}

export const PLATFORM_ROOT = "/admin";

export const OVERVIEW_ITEM: PlatformNavItem = {
    path: "/admin",
    title: "Overview",
    subtitle: "Where everything lives and what needs attention",
    icon: "◫",
    access: "observer"
};

export const PLATFORM_NAV: PlatformNavGroup[] = [
    {
        key: "agents",
        title: "Agents",
        items: [
            {
                path: "/admin/agents",
                title: "Agents",
                subtitle: "Agents composed from the library and which one serves each site / channel",
                icon: "◈",
                access: "admin"
            },
            { path: "/admin/skills", title: "Skill library", subtitle: "Versioned core, skill and tool prompts", icon: "▦", access: "admin" },
            {
                path: "/admin/agent-runtime",
                title: "Agent runtime",
                subtitle: "In-process agent backend — model, effort, indexes, LLM registry",
                icon: "◎",
                access: "admin"
            },
            {
                path: "/admin/routing",
                title: "Routing",
                subtitle: "Application title and chat backend (n8n / agent loop) per region",
                icon: "⇆",
                access: "admin"
            }
        ]
    },
    {
        key: "integrations",
        title: "Integrations",
        items: [
            {
                path: "/admin/connections",
                title: "Connections",
                subtitle: "External services the agent's tools call — URLs, keys, on/off, test",
                icon: "⛓",
                access: "admin"
            },
            { path: "/admin/models", title: "Models", subtitle: "Embedding models and n8n latency routing", icon: "▤", access: "admin" },
            { path: "/admin/search", title: "Search", subtitle: "Default tuning for /search", icon: "⌕", access: "admin" }
        ]
    },
    {
        key: "chat",
        title: "Chat experience",
        items: [
            { path: "/admin/voice", title: "Voice input", subtitle: "Availability per channel, Azure Speech, tuning, phrase list", icon: "◉", access: "admin" },
            { path: "/admin/rating", title: "Rating", subtitle: "Conversation rating — what is collected and where to read it", icon: "★", access: "admin" },
            // ENG-067
            {
                path: "/admin/ready-questions",
                title: "Ready questions",
                subtitle: "Ready questions per product type — site widget and Genie",
                icon: "❖",
                access: "admin"
            }
        ]
    },
    {
        key: "observability",
        title: "Observability",
        items: [
            { path: "/admin/analytics/overview", title: "Usage", subtitle: "Usage, timing, voice and site trends", icon: "◔", access: "observer" },
            { path: "/admin/analytics/users", title: "Users", subtitle: "Top users and drill-down entry point", icon: "◎", access: "observer" },
            { path: "/admin/analytics/sessions", title: "Sessions", subtitle: "Inspect conversations turn by turn", icon: "▣", access: "observer" },
            { path: "/admin/analytics/agents", title: "Agent comparison", subtitle: "Compare agent variants (A/B)", icon: "⇄", access: "observer" },
            {
                path: "/admin/analytics/keycloak",
                title: "Store directory",
                subtitle: "Keycloak sync — resolve usernames to stores",
                icon: "⌂",
                access: "observer"
            },
            { path: "/admin/latency", title: "Latency history", subtitle: "Probe latency over time", icon: "◷", access: "admin" },
            { path: "/admin/audit", title: "Audit", subtitle: "Last 50 admin changes", icon: "☰", access: "admin" }
        ]
    }
];

export const LEGACY_REDIRECTS: Record<string, string> = {
    "/analytics": "/admin/analytics/overview",
    "/dashboard": "/admin/analytics/overview"
};

export const canAccess = (access: PlatformAccess, hasRole: (role: UserRole) => boolean): boolean =>
    access === "admin" ? hasRole(UserRole.ADMIN) : hasRole(UserRole.ADMIN) || hasRole(UserRole.ANALYTICS);

const ALL_ITEMS: PlatformNavItem[] = [OVERVIEW_ITEM, ...PLATFORM_NAV.flatMap(g => g.items)];

/** The nav item a pathname belongs to (longest matching prefix), with its group title for the breadcrumb. */
export const findNavItem = (pathname: string): { item: PlatformNavItem; group: string | null } | null => {
    let best: { item: PlatformNavItem; group: string | null } | null = null;
    for (const item of ALL_ITEMS) {
        const matches = item.path === PLATFORM_ROOT ? pathname === PLATFORM_ROOT || pathname === `${PLATFORM_ROOT}/` : pathname.startsWith(item.path);
        if (matches && (!best || item.path.length > best.item.path.length)) {
            best = { item, group: PLATFORM_NAV.find(g => g.items.includes(item))?.title ?? null };
        }
    }
    return best;
};
